import { useRef, useState } from "react";
import { Upload, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

interface SimulationUploadZoneProps {
  selectedFile: File | null;
  onFileSelect: (file: File | null) => void;
}

export function SimulationUploadZone({ selectedFile, onFileSelect }: SimulationUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingPickLabel, setPendingPickLabel] = useState<"Text Content" | "Schema Markup" | null>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const openFilePicker = (label: "Text Content" | "Schema Markup") => {
    setPendingPickLabel(label);
    fileInputRef.current?.click();
  };

  return (
    <div className="mt-6 rounded-xl border" style={{ backgroundColor: "var(--background)", borderColor: "var(--border)", boxShadow: "var(--elevation-sm)" }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0] || null;
          onFileSelect(f);
          setPendingPickLabel(null);
        }}
      />

      <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: "var(--signal-blue-10)", border: "1px solid var(--signal-blue-20)" }}>
            <Upload className="w-4 h-4" style={{ color: "var(--signal-blue)" }} />
          </div>
          <div>
            <h4 style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)", marginBottom: "2px" }}>
              Upload Document (Optional)
            </h4>
            <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
              Upload details for better simulation. Supported: .pdf, .docx, .doc, .txt, .md, .json, .html
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div
          className="border-2 border-dashed rounded-xl p-6 text-center transition-all"
          style={{
            borderColor: isDraggingOver ? "var(--signal-blue)" : "var(--border)",
            backgroundColor: isDraggingOver ? "var(--signal-blue-5)" : "var(--muted-30)",
            minHeight: "220px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDraggingOver(true);
          }}
          onDragLeave={() => setIsDraggingOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDraggingOver(false);
            const file = e.dataTransfer.files?.[0];
            if (!file) return;
            onFileSelect(file);
          }}
          onClick={() => openFilePicker("Text Content")}
        >
          <Upload className="w-10 h-10 mb-3" style={{ color: isDraggingOver ? "var(--signal-blue)" : "var(--muted-foreground)" }} />
          <h5 style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--foreground)", marginBottom: "8px" }}>
            {isDraggingOver ? "Drop to select file" : "Drag & drop file"}
          </h5>
          <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginBottom: "12px" }}>
            or click to browse
          </p>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openFilePicker("Text Content"); }}>
              Text Content
            </Button>
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openFilePicker("Schema Markup"); }}>
              Schema Markup
            </Button>
          </div>

          {selectedFile && (
            <div className="mt-4">
              <Badge variant="outline" style={{ backgroundColor: "var(--background)" }}>
                Selected: {selectedFile.name}
              </Badge>
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 rounded-lg p-3" style={{ backgroundColor: "var(--spectral-violet-4)", borderLeft: `2px solid var(--spectral-violet)` }}>
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--spectral-violet)" }} />
            <div>
              <p style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", color: "var(--spectral-violet)", margin: 0, marginBottom: "4px" }}>
                Pro tip
              </p>
              <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", lineHeight: "1.5", margin: 0 }}>
                Pick a doc that contains your product/brand facts so the model can ground its estimate.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}